import { useCallback, useLayoutEffect, useMemo, useState, useRef, createContext } from 'react';
import { useNavigation } from '@hotjar/navigation';
import { useDebounce } from '@hotjar/webapp/hooks/useDebounce';
import { useNavigate, useLocation } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import type { ListChildComponentProps } from 'react-window';
import { VariableSizeList } from 'react-window';
import { useSessionStorage } from '@hotjar/insights-common/hooks/useStorage';
import { partition } from 'lodash-es';
import { useTranslation } from '@hotjar/translations';
import type { Widget } from '..';
import { useCopyLink } from '../../../../hooks/useCopyLink';
import { EntityDeleteModal } from '../../EntityActions/EntityDeleteModal';
import { Row } from './WidgetListSideBarRow';
import { SideBar } from '..';
import type { SideBarItemActions } from '../SideBarItem';

const DEFAULT_ROW_SIZE = 32;

export const ROW_GUTTER_SIZE = 4;
const ROW_HEADER_SIZE = 65;

const FILTER_QUERY_SESSION_STORAGE_KEY_PREFIX = 'widget_sidebar_query_';
const ACTIVE_LIST_TOGGLE_SESSION_KEY_PREFIX = 'active_widget_list_toggle_';
const INACTIVE_LIST_TOGGLE_SESSION_KEY_PREFIX = 'inactive_widget_list_toggle_';

type EmptyRowData = {
  type: 'empty';
  text: string;
};

type WidgetRowData = {
  type: 'widget';
  widget: Widget;
  widgetType: 'survey' | 'feedback';
  href: string;
  onActionSelected: (action: SideBarItemActions, widget: Widget) => void;
  hasUpdatePermission: boolean;
  hasDuplicatePermission: boolean;
  hasDeletePermission: boolean;
};

type AllWidgetsRowData = {
  type: 'all';
  count: number;
  href: string;
};

type CategoryRowData = {
  type: 'category';
  title: string;
  onClick: () => void;
  isExpanded: boolean;
};

export type RowData = WidgetRowData | CategoryRowData | EmptyRowData | AllWidgetsRowData;

// eslint-disable-next-line react/no-unused-prop-types
type SizeProps = { height: number; width: number };

export const DynamicListContext = createContext<
  Partial<{ setSize: (index: number, size: number) => void }>
>({});

const RenderVirtualRow = (props: ListChildComponentProps<RowData[]>) => <Row {...props} />;

export interface WidgetListSideBarProps {
  widgets: Widget[] | undefined;
  selectedWidget: Widget | undefined;
  onDeleteWidget: (widget: Widget) => Promise<void>;
  onRenameWidget: (widgetId: number, newName: string) => Promise<void>;
  getWidgetRoute: (widget?: Widget) => string;
  getAllWidgetsRoute?: () => string;
  getWidgetEditRoute: (widget: Widget) => string;
  getWidgetDuplicateRoute: (widget: Widget) => string;
  widgetType: 'feedback' | 'survey';

  hasUpdatePermission: boolean;
  hasDuplicatePermission: boolean;
  hasDeletePermission: boolean;
  isSurveyList?: boolean;
}

interface ModalAction {
  type: 'delete' | 'move';
  target: Widget;
  isPending: boolean;
}

export const WidgetListSideBar = ({
  // TODO Typescript is incorrectly reporting a prop validation error
  // we can try removing this eslint-disable after typescript is updated
  /* eslint-disable react/prop-types */
  widgets,
  selectedWidget,
  onDeleteWidget,
  onRenameWidget,
  getWidgetRoute,
  getWidgetEditRoute,
  getAllWidgetsRoute,
  getWidgetDuplicateRoute,
  widgetType,

  hasUpdatePermission,
  hasDuplicatePermission,
  hasDeletePermission,
  isSurveyList,
}: WidgetListSideBarProps): JSX.Element => {
  const { setIsSidebarMinimized } = useNavigation();
  const { t } = useTranslation('feedback', { keyPrefix: 'sidebar.widgets' });

  useLayoutEffect(() => {
    setIsSidebarMinimized(true);
  }, [setIsSidebarMinimized]);

  const [searchText, setSearchText] = useSessionStorage<string>(
    FILTER_QUERY_SESSION_STORAGE_KEY_PREFIX + widgetType,
    '',
  );
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);
  const { success, error } = useNotify();
  const copyLink = useCopyLink();
  const navigate = useNavigate();
  const location = useLocation();

  const readableWidgetType = widgetType === 'survey' ? 'survey' : 'widget';

  const [modalAction, setModalAction] = useState<ModalAction | null>(null);
  const [isActiveExpanded, setIsActiveExpanded] = useSessionStorage<boolean>(
    ACTIVE_LIST_TOGGLE_SESSION_KEY_PREFIX + widgetType,
    true,
  );
  const [isInactiveExpanded, setIsInactiveExpanded] = useSessionStorage<boolean>(
    INACTIVE_LIST_TOGGLE_SESSION_KEY_PREFIX + widgetType,
    true,
  );

  const onSearchTextChange = useCallback(
    (newSearchText: string) => {
      setSearchText(newSearchText);
    },
    [setSearchText],
  );

  useDebounce(
    () => {
      setDebouncedSearchText(searchText.toLocaleLowerCase());
    },
    300,
    [searchText],
  );

  const copyLinkToClipboard = useCallback(
    async (widget: Widget) => {
      copyLink(window.location.origin + getWidgetRoute(widget));
    },
    [copyLink, getWidgetRoute],
  );

  const editWidget = useCallback(
    (widget: Widget) => {
      navigate(getWidgetEditRoute(widget));
    },
    [getWidgetEditRoute, navigate],
  );

  const duplicateWidget = useCallback(
    (widget: Widget) => {
      navigate(getWidgetDuplicateRoute(widget));
    },
    [getWidgetDuplicateRoute, navigate],
  );

  const goToNextWidgetIfNeeded = useCallback(
    (widget: Widget) => {
      // If we are deleting the currently open widget - navigate
      if (location.pathname === getWidgetRoute(widget)) {
        // If possible, navigate to the first widget that isn't the current widget
        const nextWidget = widgets?.find((otherWidget) => otherWidget.id !== widget.id);
        if (nextWidget) {
          navigate(getWidgetRoute(nextWidget));
        }
      }
    },
    [widgets, location, getWidgetRoute, navigate],
  );

  const confirmDeleteWidget = useCallback(
    async (widget: Widget | undefined) => {
      if (!widget) return;
      setModalAction((prevState) => (prevState ? { ...prevState, isPending: true } : null));
      try {
        await onDeleteWidget(widget);

        goToNextWidgetIfNeeded(widget);

        success(t(`deleted-${readableWidgetType}`));
      } catch {
        error(t(`delete-error-${readableWidgetType}`));
      } finally {
        setModalAction(null);
      }
    },
    [onDeleteWidget, success, error, setModalAction, goToNextWidgetIfNeeded, readableWidgetType, t],
  );

  const [renamingWidget, setRenamingWidget] = useState<Widget | null>(null);

  const handleRenameWidget = useCallback(
    async (widgetId: number | undefined, newName: string) => {
      try {
        if (widgetId === undefined) return;

        await onRenameWidget(widgetId, newName);

        success(t(`renamed-${readableWidgetType}`));
      } catch {
        error(t(`rename-error-${readableWidgetType}`));
      } finally {
        setRenamingWidget(null);
      }
    },
    [onRenameWidget, success, error, readableWidgetType, t],
  );

  const onActionSelected = useCallback(
    (action: SideBarItemActions, widget: Widget) => {
      switch (action) {
        case 'copy_link':
          copyLinkToClipboard(widget);
          return;
        case 'edit':
          editWidget(widget);
          return;
        case 'delete':
          setModalAction({ target: widget, type: action, isPending: false });
          return;
        case 'duplicate':
          duplicateWidget(widget);
          return;
        case 'rename':
          setRenamingWidget(widget);
      }
    },
    [editWidget, copyLinkToClipboard, duplicateWidget, setModalAction],
  );

  const categoryActive: CategoryRowData = useMemo(
    () => ({
      type: 'category',
      title: t(`active-${readableWidgetType}`),
      isExpanded: isActiveExpanded,
      onClick: () => {
        setIsActiveExpanded(!isActiveExpanded);
      },
    }),
    [isActiveExpanded, readableWidgetType, setIsActiveExpanded, t],
  );

  const categoryInactive: CategoryRowData = useMemo(
    () => ({
      type: 'category',
      title: t(`inactive-${readableWidgetType}`),
      isExpanded: isInactiveExpanded,
      onClick: () => {
        setIsInactiveExpanded(!isInactiveExpanded);
      },
    }),
    [isInactiveExpanded, readableWidgetType, setIsInactiveExpanded],
  );

  const rowData = useMemo<RowData[]>(() => {
    if (!widgets) return [];

    const visibleWidgets = widgets.filter((widget) => {
      if ((widget.enabled && !isActiveExpanded) || (!widget.enabled && !isInactiveExpanded)) {
        return false;
      }
      if (debouncedSearchText.length === 0) {
        return true;
      }
      return widget.name.toLocaleLowerCase().indexOf(debouncedSearchText) !== -1;
    });

    const groupedWidgets = partition(visibleWidgets, (widget) => widget.enabled);

    groupedWidgets.forEach((group) =>
      group.sort((a, b) => b.created_epoch_time - a.created_epoch_time),
    );

    const createRow = (widget: Widget): WidgetRowData => ({
      type: 'widget',
      widget,
      widgetType,
      href: getWidgetRoute(widget),
      onActionSelected,
      hasDeletePermission,
      hasDuplicatePermission,
      hasUpdatePermission,
    });

    const createGroup = (
      ungroupedWidgets: Widget[],
      typeName: string,
    ): WidgetRowData[] | EmptyRowData[] => {
      if (ungroupedWidgets.length !== 0) {
        return ungroupedWidgets.map(createRow);
      }

      if (debouncedSearchText) {
        return [{ type: 'empty', text: t(`${readableWidgetType}-${typeName}-empty-search`) }];
      }

      return [{ type: 'empty', text: t(`${readableWidgetType}-${typeName}-empty`) }];
    };

    const activeWidgetRowData = isActiveExpanded ? createGroup(groupedWidgets[0], 'active') : [];
    const inactiveWidgetRowData = isInactiveExpanded
      ? createGroup(groupedWidgets[1], 'inactive')
      : [];
    if (widgetType === 'survey' && widgets.length > 0 && getAllWidgetsRoute) {
      const allSurveys: AllWidgetsRowData = {
        type: 'all',
        count: widgets.length,
        href: getAllWidgetsRoute(),
      };
      return [
        allSurveys,
        categoryActive,
        ...activeWidgetRowData,
        categoryInactive,
        ...inactiveWidgetRowData,
      ];
    }
    return [categoryActive, ...activeWidgetRowData, categoryInactive, ...inactiveWidgetRowData];
  }, [
    widgets,
    widgetType,
    categoryActive,
    categoryInactive,
    isActiveExpanded,
    isInactiveExpanded,
    debouncedSearchText,
    readableWidgetType,
    getWidgetRoute,
    getAllWidgetsRoute,
    onActionSelected,
    hasDeletePermission,
    hasDuplicatePermission,
    hasUpdatePermission,
    t,
  ]);

  const listRef = useRef<VariableSizeList | null>(null);

  const sizeMap = useRef<Partial<Record<number, number>>>({});

  useLayoutEffect(() => {
    if (listRef.current && selectedWidget?.id !== undefined) {
      const index = rowData.findIndex(
        (e) => e.type === 'widget' && e.widget.id === selectedWidget.id,
      );
      if (index !== undefined) {
        listRef.current.scrollToItem(index, 'smart');
      }
    }
  }, [rowData, selectedWidget?.id]);

  const setSize = useCallback((index: number, size: number) => {
    // Performance: Only update the sizeMap and reset cache if an actual value changed
    if (
      sizeMap.current[index] === size ||
      (sizeMap.current[index] === undefined && size === DEFAULT_ROW_SIZE)
    )
      return;

    sizeMap.current = { ...sizeMap.current, [index]: size };
    if (listRef.current) {
      // Clear cached data and rerender
      listRef.current.resetAfterIndex(0);
    }
  }, []);

  const getSize = useCallback((index) => {
    return (sizeMap.current[index] ?? DEFAULT_ROW_SIZE) + (index !== 0 ? ROW_GUTTER_SIZE : 0);
  }, []);

  const calcEstimatedSize = useCallback(() => {
    return ROW_HEADER_SIZE;
  }, []);

  const sidebarContext = useMemo(() => {
    return {
      selectedWidgetId: isSurveyList === true ? 0 : selectedWidget?.id,
      renamingWidgetId: renamingWidget?.id,
      rename: handleRenameWidget,
      cancelRename: () => setRenamingWidget(null),
    };
  }, [selectedWidget, renamingWidget, handleRenameWidget, isSurveyList]);

  return (
    <>
      <SideBar
        searchText={searchText}
        onSearchTextChange={onSearchTextChange}
        searchDisabled={widgets !== undefined && widgets.length === 0}
        sidebarContext={sidebarContext}
      >
        <DynamicListContext.Provider
          // FIXME: This creates a new object on every render and may create unnecessary rerenders.
          //        Fix this or add a comment to explain why it's desired.
          //        More details: https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/jsx-no-constructed-context-values.md
          // eslint-disable-next-line react/jsx-no-constructed-context-values
          value={{ setSize }}
        >
          <AutoSizer>
            {({ height, width }: SizeProps) => (
              <VariableSizeList<RowData[]>
                ref={listRef}
                height={height}
                width={width}
                itemSize={getSize}
                itemCount={rowData.length}
                itemData={rowData}
                overscanCount={20}
                estimatedItemSize={calcEstimatedSize()}
              >
                {RenderVirtualRow}
              </VariableSizeList>
            )}
          </AutoSizer>
        </DynamicListContext.Provider>
      </SideBar>
      {modalAction?.type === 'delete' && (
        <EntityDeleteModal
          entityName={modalAction.target.name}
          entityType={readableWidgetType}
          isDeletePending={!!modalAction.isPending}
          isOpen
          onDelete={() => confirmDeleteWidget(modalAction.target)}
          onClose={() => setModalAction(null)}
        />
      )}
    </>
  );
  /* eslint-enable react/prop-types */
};
